import { memo } from 'react';
import styled from 'styled-components';
import type { AvatarProps, AvatarSizes } from '@frameio/vapor';
import { Avatar } from '@frameio/vapor';
import { DefaultAvatar } from './DefaultAvatar';
import { InvitedToAvatar } from './InvitedToAvatar';
import { WorkfrontDefaultAvatar } from './WorkfrontDefaultAvatar';

type WorkspaceAvatars = 'invitedTo';

export type WorkspaceAvatarProps = {
  alt: string;
  avatarUrl?: string | null;
  avatar?: WorkspaceAvatars;
  isWorkfront?: boolean;
  size?: AvatarSizes;
};

/**
 * Conditionally renders the Avatar component, based on the presence of an
 * avatar URL.
 * @returns an Avatar styled component
 */
const getAvatar = ({
  alt,
  avatarUrl,
  isWorkfront,
  size = 40 as AvatarSizes,
  avatar,
}: WorkspaceAvatarProps) => {
  const workspaceAvatarProps: AvatarProps = {
    background: 'transparent',
    shape: 'square',
    ...(alt && { role: 'img' }),
  };

  if (avatar === 'invitedTo') {
    return (
      <Avatar {...workspaceAvatarProps} alt={alt} size={size}>
        <InvitedToAvatar height={size} width={size} />
      </Avatar>
    );
  }

  if (isWorkfront) {
    return (
      <Avatar {...workspaceAvatarProps} alt={alt} size={size}>
        <WorkfrontDefaultAvatar height={size} width={size} />
      </Avatar>
    );
  }

  /**
   * Note: If using an avatarURL for an icon avatar created by frame.io
   * and not coming from a user-uploaded image, please make sure that the
   * colors work in all themes or create a component rendering the
   * svg so that it can use tokens, similar to the `InvitedToAvatar`.
   * */
  if (avatarUrl) {
    return (
      <Avatar {...workspaceAvatarProps} alt={alt} size={size} src={avatarUrl}>
        <DefaultAvatar height={size} width={size} />
      </Avatar>
    );
  }

  // Default
  return (
    <Avatar {...workspaceAvatarProps} alt={alt} size={size}>
      <DefaultAvatar height={size} width={size} />
    </Avatar>
  );
};

const StyledWorkspaceAvatarContainer = styled.div`
  display: inline-flex;
`;

export const WorkspaceAvatar = memo(
  ({
    alt,
    avatarUrl,
    size,
    avatar,
    isWorkfront,
    ...rest
  }: WorkspaceAvatarProps) => {
    return (
      <StyledWorkspaceAvatarContainer {...rest}>
        {getAvatar({ alt, avatar, avatarUrl, isWorkfront, size })}
      </StyledWorkspaceAvatarContainer>
    );
  }
);
